import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Trophy,
  Crown,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Calendar,
  TrendingUp,
  Image,
  MapPin,
  User,
  Trash2,
  MessageCircle,
  Send,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAppStore } from '../store';
import { uploadImages, validateImageFile, createImagePreview, revokeImagePreview } from '../services/storage.service';
import Lightbox from '@/components/Lightbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import type { BragPost } from '../types';

const GROUNDS_ID = 'grounds';
const GROUNDS_NAME = 'Umgebung / Grundstück';

const QUICK_COMMENTS = [
  'Super gemacht!',
  'Toll, danke dir!',
  'Sieht klasse aus!',
  'Wow, top Arbeit!',
  'Danke für deinen Einsatz!',
];

const BragBoardPage: React.FC = () => {
  const { bragPosts, homes, users, currentUser, addBragPost, deleteBragPost, reactToBrag, commentOnBrag, deleteBragComment } = useAppStore();
  const [view, setView] = useState<'posts' | 'leaderboard'>('posts');
  const [sortBy, setSortBy] = useState<'recent' | 'popular'>('recent');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BragPost | null>(null);
  const [commentText, setCommentText] = useState('');
  const [isCommenting, setIsCommenting] = useState(false);
  const [newPost, setNewPost] = useState({
    homeId: '',
    imageUrl: '',
    caption: '',
  });
  const [bragImageFiles, setBragImageFiles] = useState<File[]>([]);
  const [bragImagePreviews, setBragImagePreviews] = useState<string[]>([]);
  const [isUploadingBrag, setIsUploadingBrag] = useState(false);
  const [detailIdx, setDetailIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const MAX_BRAG_IMAGES = 10;

  // Normalize a post's images: prefer imageUrls, fall back to the single imageUrl.
  const postImages = (post: BragPost): string[] =>
    post.imageUrls && post.imageUrls.length > 0 ? post.imageUrls : post.imageUrl ? [post.imageUrl] : [];

  // Revoke any outstanding preview object URLs if the page unmounts before the form is closed.
  const previewUrlsRef = useRef<string[]>([]);
  previewUrlsRef.current = bragImagePreviews;
  useEffect(() => () => { previewUrlsRef.current.forEach(revokeImagePreview); }, []);

  const sortedPosts = useMemo(() => {
    const posts = [...bragPosts];

    if (sortBy === 'recent') {
      return posts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    } else {
      return posts.sort((a, b) => {
        const aLikes = Object.values(a.reactions).filter(r => r === 'like').length;
        const bLikes = Object.values(b.reactions).filter(r => r === 'like').length;
        return bLikes - aLikes;
      });
    }
  }, [bragPosts, sortBy]);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    bragPosts.forEach(post => {
      years.add(post.timestamp.getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [bragPosts]);

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === new Date().getFullYear() && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const leaderboardData = useMemo(() => {
    const yearPosts = bragPosts.filter(post => post.timestamp.getFullYear() === selectedYear);
    const userStats = new Map<string, { totalBrags: number; totalLikes: number; totalDislikes: number }>();

    yearPosts.forEach(post => {
      const existing = userStats.get(post.userId) || { totalBrags: 0, totalLikes: 0, totalDislikes: 0 };
      const likes = Object.values(post.reactions).filter(r => r === 'like').length;
      const dislikes = Object.values(post.reactions).filter(r => r === 'dislike').length;

      userStats.set(post.userId, {
        totalBrags: existing.totalBrags + 1,
        totalLikes: existing.totalLikes + likes,
        totalDislikes: existing.totalDislikes + dislikes,
      });
    });

    return Array.from(userStats.entries())
      .map(([userId, stats]) => ({
        userId,
        userName: users.find(u => u.id === userId)?.name || 'Unknown User',
        ...stats,
      }))
      .sort((a, b) => b.totalLikes - a.totalLikes);
  }, [bragPosts, users, selectedYear]);

  const bragKing = leaderboardData.length > 0 ? leaderboardData[0] : null;

  const resetBragForm = () => {
    setNewPost({ homeId: '', imageUrl: '', caption: '' });
    bragImagePreviews.forEach(revokeImagePreview);
    setBragImageFiles([]);
    setBragImagePreviews([]);
  };

  const handleAddPost = async () => {
    if (!newPost.homeId || (!newPost.imageUrl && bragImageFiles.length === 0) || !newPost.caption || !currentUser) {
      alert('Bitte alle Felder ausfüllen');
      return;
    }

    setIsUploadingBrag(true);

    try {
      // Upload selected files, or fall back to a single pasted URL
      const imageUrls = bragImageFiles.length > 0
        ? await uploadImages(bragImageFiles, 'brag-posts')
        : [newPost.imageUrl];

      await addBragPost({
        userId: currentUser.id,
        homeId: newPost.homeId,
        imageUrl: imageUrls[0],
        imageUrls,
        caption: newPost.caption,
        reactions: {},
      });

      resetBragForm();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding brag post:', error);
      alert('Beitrag konnte nicht erstellt werden. Bitte erneut versuchen.');
    } finally {
      setIsUploadingBrag(false);
    }
  };

  const handleBragImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file(s)
    if (files.length === 0) return;

    const room = MAX_BRAG_IMAGES - bragImageFiles.length;
    if (room <= 0) {
      alert(`Maximal ${MAX_BRAG_IMAGES} Bilder pro Beitrag.`);
      return;
    }

    const accepted: File[] = [];
    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        alert(`${file.name}: ${validation.error}`);
        continue;
      }
      accepted.push(file);
    }

    const toAdd = accepted.slice(0, room);
    if (accepted.length > room) {
      alert(`Maximal ${MAX_BRAG_IMAGES} Bilder pro Beitrag.`);
    }
    if (toAdd.length === 0) return;

    setBragImageFiles((prev) => [...prev, ...toAdd]);
    setBragImagePreviews((prev) => [...prev, ...toAdd.map(createImagePreview)]);
  };

  const handleRemoveBragImage = (index: number) => {
    setBragImageFiles((prev) => prev.filter((_, i) => i !== index));
    setBragImagePreviews((prev) => {
      const removed = prev[index];
      if (removed) revokeImagePreview(removed);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Beitrag wirklich löschen?')) return;
    await deleteBragPost(postId);
  };

  const handleReaction = (postId: string, reaction: 'like' | 'dislike') => {
    if (!currentUser) return;
    reactToBrag(postId, currentUser.id, reaction);
  };

  const getUserReaction = (post: BragPost) => {
    if (!currentUser) return null;
    return post.reactions[currentUser.id] || null;
  };

  const getReactionCount = (post: BragPost, type: 'like' | 'dislike') => {
    return Object.values(post.reactions).filter(r => r === type).length;
  };

  useEffect(() => {
    setDetailIdx(0);
    setLightboxOpen(false);
  }, [selectedPost?.id]);

  // Keep selectedPost in sync with live data (reactions/comments update in real-time)
  const liveSelectedPost = selectedPost ? bragPosts.find(p => p.id === selectedPost.id) ?? null : null;

  const handleComment = async (text: string) => {
    if (!currentUser || !liveSelectedPost || !text.trim()) return;
    setIsCommenting(true);
    await commentOnBrag(liveSelectedPost.id, currentUser.id, text.trim());
    setCommentText('');
    setIsCommenting(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!liveSelectedPost) return;
    await deleteBragComment(liveSelectedPost.id, commentId);
  };

  const getCommentsList = (post: BragPost) => {
    if (!post.comments) return [];
    return Object.entries(post.comments)
      .map(([id, c]) => ({ ...c, id }))
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-500'; // Gold
    if (rank === 2) return 'text-gray-400'; // Silver
    if (rank === 3) return 'text-orange-600'; // Bronze
    return 'text-muted-foreground';
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Brag Board</h1>
        <p className="text-muted-foreground">Zeig, was du an unseren Unterkünften verbessert oder beigetragen hast — und sieh, was die anderen geleistet haben!</p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-border pb-2">
        <button
          className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors border-b-2 ${
            view === 'posts'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-primary hover:bg-accent'
          }`}
          onClick={() => setView('posts')}
        >
          <Image size={16} />
          Alle Beiträge
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors border-b-2 ${
            view === 'leaderboard'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-primary hover:bg-accent'
          }`}
          onClick={() => setView('leaderboard')}
        >
          <Trophy size={16} />
          Rangliste
        </button>
      </div>

      {view === 'posts' && (
        <>
          <div className="flex justify-between items-center mb-8">
            <div className="flex gap-2">
              <button
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors rounded-md ${
                  sortBy === 'recent'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
                onClick={() => setSortBy('recent')}
              >
                <Calendar size={16} />
                Neueste
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors rounded-md ${
                  sortBy === 'popular'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
                onClick={() => setSortBy('popular')}
              >
                <TrendingUp size={16} />
                Beliebt
              </button>
            </div>

            {currentUser && (
              <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                <Plus size={16} />
                Beitrag erstellen
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPosts.map((post) => {
              const home = homes.find(h => h.id === post.homeId);
              const homeName = post.homeId === GROUNDS_ID ? GROUNDS_NAME : (home?.name || 'Unbekannte Unterkunft');
              const user = users.find(u => u.id === post.userId);
              const userReaction = getUserReaction(post);
              const likes = getReactionCount(post, 'like');
              const dislikes = getReactionCount(post, 'dislike');
              const images = postImages(post);

              return (
                <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <button
                    type="button"
                    className="relative w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setSelectedPost(post)}
                  >
                    <img src={images[0]} alt="Brag" className="w-full h-[300px] object-cover" />
                    {images.length > 1 && (
                      <span className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-full">
                        <Image size={12} />
                        {images.length}
                      </span>
                    )}
                  </button>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-primary mb-1">
                          {user?.name || 'Unbekannter Benutzer'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {homeName}
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-foreground leading-relaxed mb-4">
                      {post.caption}
                    </p>

                    <div className="flex justify-between items-center pt-3 border-t">
                      <div className="flex gap-2">
                        <button
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                            userReaction === 'like'
                              ? 'bg-primary/10 text-primary'
                              : 'bg-transparent text-muted-foreground hover:bg-accent hover:text-primary'
                          }`}
                          onClick={() => handleReaction(post.id, 'like')}
                        >
                          <ThumbsUp size={16} />
                          {likes}
                        </button>
                        <button
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                            userReaction === 'dislike'
                              ? 'bg-primary/10 text-primary'
                              : 'bg-transparent text-muted-foreground hover:bg-accent hover:text-primary'
                          }`}
                          onClick={() => handleReaction(post.id, 'dislike')}
                        >
                          <ThumbsDown size={16} />
                          {dislikes}
                        </button>
                        <button
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors text-muted-foreground hover:bg-accent hover:text-primary"
                          onClick={() => setSelectedPost(post)}
                        >
                          <MessageCircle size={16} />
                          {post.comments ? Object.keys(post.comments).length : 0}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-muted-foreground">
                          {post.timestamp.toLocaleDateString('de-DE')}
                        </div>
                        {currentUser?.isAdmin && (
                          <button
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs text-destructive hover:bg-destructive/10 transition-colors"
                            onClick={() => handleDeletePost(post.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {view === 'leaderboard' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Brag Rangliste</CardTitle>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </CardHeader>
          <CardContent>
            {bragKing && (
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                  <Crown size={20} className="text-yellow-500" />
                  Brag King {selectedYear}
                </h3>
                <Badge className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white border-none font-bold text-lg px-4 py-1">
                  {bragKing.userName}
                </Badge>
                <p className="text-sm text-muted-foreground mt-2">
                  {bragKing.totalBrags} Beiträge • {bragKing.totalLikes} Likes
                </p>
              </div>
            )}

            <div className="h-px bg-border my-6" />

            {leaderboardData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rang</TableHead>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Beiträge</TableHead>
                    <TableHead>Likes</TableHead>
                    <TableHead>Dislikes</TableHead>
                    <TableHead>Punkte</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboardData.map((entry, index) => (
                    <TableRow key={entry.userId}>
                      <TableCell className={`font-semibold ${getRankColor(index + 1)}`}>
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {entry.userName}
                      </TableCell>
                      <TableCell>{entry.totalBrags}</TableCell>
                      <TableCell className="text-green-600">+{entry.totalLikes}</TableCell>
                      <TableCell className="text-red-600">-{entry.totalDislikes}</TableCell>
                      <TableCell className={`font-semibold ${
                        entry.totalLikes - entry.totalDislikes >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {entry.totalLikes - entry.totalDislikes >= 0 ? '+' : ''}
                        {entry.totalLikes - entry.totalDislikes}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Trophy size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Keine Daten für {selectedYear}</h3>
                <p className="text-muted-foreground">Keine Beiträge für das gewählte Jahr gefunden.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) resetBragForm(); setShowAddModal(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neuen Beitrag erstellen</DialogTitle>
            <p className="text-sm text-muted-foreground">Zeig, was du an der Unterkunft verbessert oder beigetragen hast.</p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <MapPin size={16} />
                Ort
              </label>
              <select
                value={newPost.homeId}
                onChange={(e) => setNewPost({ ...newPost, homeId: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Ort auswählen</option>
                {homes.map(home => (
                  <option key={home.id} value={home.id}>{home.name}</option>
                ))}
                <option value={GROUNDS_ID}>{GROUNDS_NAME}</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bragImage" className="flex items-center gap-2">
                <Image size={16} />
                Bilder hochladen
              </Label>
              <Input
                id="bragImage"
                type="file"
                accept="image/*"
                multiple
                onChange={handleBragImageChange}
                className="cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">
                Mehrere Bilder möglich (bis zu {MAX_BRAG_IMAGES}, je max. 5 MB, JPEG/PNG/WebP)
              </p>
              {bragImagePreviews.length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {bragImagePreviews.map((preview, index) => (
                    <div key={preview} className="relative group">
                      <img
                        src={preview}
                        alt={`Vorschau ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveBragImage(index)}
                        className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 transition-colors"
                        aria-label="Bild entfernen"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bragImageUrl" className="flex items-center gap-2">
                <Image size={16} />
                Oder Bild-URL einfügen
              </Label>
              <Input
                id="bragImageUrl"
                type="url"
                value={newPost.imageUrl}
                onChange={(e) => setNewPost({ ...newPost, imageUrl: e.target.value })}
                placeholder="https://example.com/image.jpg"
                disabled={bragImageFiles.length > 0}
              />
              <p className="text-xs text-muted-foreground">
                Alternative zum Datei-Upload
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="caption" className="flex items-center gap-2">
                <User size={16} />
                Beschreibung
              </Label>
              <textarea
                id="caption"
                value={newPost.caption}
                onChange={(e) => setNewPost({ ...newPost, caption: e.target.value })}
                placeholder="z.B. Neue Gartenmöbel aufgestellt, Küche renoviert, Terrasse gestrichen…"
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { resetBragForm(); setShowAddModal(false); }}>
              Abbrechen
            </Button>
            <Button
              onClick={handleAddPost}
              disabled={!newPost.homeId || (!newPost.imageUrl && bragImageFiles.length === 0) || !newPost.caption || isUploadingBrag}
            >
              {isUploadingBrag ? 'Wird hochgeladen...' : 'Beitrag erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brag Detail / Lightbox Dialog */}
      <Dialog open={!!liveSelectedPost} onOpenChange={(open) => { if (!open) { setSelectedPost(null); setCommentText(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {liveSelectedPost && (() => {
            const detailHome = homes.find(h => h.id === liveSelectedPost.homeId);
            const detailHomeName = liveSelectedPost.homeId === GROUNDS_ID ? GROUNDS_NAME : (detailHome?.name || 'Unbekannte Unterkunft');
            const detailUser = users.find(u => u.id === liveSelectedPost.userId);
            const detailReaction = getUserReaction(liveSelectedPost);
            const detailLikes = getReactionCount(liveSelectedPost, 'like');
            const detailDislikes = getReactionCount(liveSelectedPost, 'dislike');
            const comments = getCommentsList(liveSelectedPost);
            const detailImages = postImages(liveSelectedPost);
            const safeIdx = Math.min(detailIdx, detailImages.length - 1);

            return (
              <>
                <div className="relative bg-black">
                  <img
                    src={detailImages[safeIdx]}
                    alt="Brag"
                    className="w-full max-h-[400px] object-contain cursor-zoom-in"
                    onClick={() => setLightboxOpen(true)}
                  />
                  {detailImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setDetailIdx((i) => (i <= 0 ? detailImages.length - 1 : i - 1))}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                        aria-label="Vorheriges Bild"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        onClick={() => setDetailIdx((i) => (i >= detailImages.length - 1 ? 0 : i + 1))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                        aria-label="Nächstes Bild"
                      >
                        <ChevronRight size={20} />
                      </button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        {safeIdx + 1} / {detailImages.length}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {/* Author & meta */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-primary">{detailUser?.name || 'Unbekannter Benutzer'}</span>
                      <Badge variant="secondary" className="text-xs">{detailHomeName}</Badge>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">{liveSelectedPost.caption}</p>
                    <p className="text-xs text-muted-foreground mt-1">{liveSelectedPost.timestamp.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                  </div>

                  {/* Reactions */}
                  <div className="flex gap-2 pb-3 border-b">
                    <button
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                        detailReaction === 'like'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-secondary text-muted-foreground hover:bg-accent hover:text-primary'
                      }`}
                      onClick={() => handleReaction(liveSelectedPost.id, 'like')}
                    >
                      <ThumbsUp size={16} />
                      {detailLikes}
                    </button>
                    <button
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                        detailReaction === 'dislike'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-secondary text-muted-foreground hover:bg-accent hover:text-primary'
                      }`}
                      onClick={() => handleReaction(liveSelectedPost.id, 'dislike')}
                    >
                      <ThumbsDown size={16} />
                      {detailDislikes}
                    </button>
                  </div>

                  {/* Comments */}
                  <div>
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                      <MessageCircle size={15} />
                      Kommentare {comments.length > 0 && `(${comments.length})`}
                    </h4>

                    {comments.length > 0 ? (
                      <div className="space-y-3 mb-4">
                        {comments.map((c) => {
                          const commentUser = users.find(u => u.id === c.userId);
                          const canDelete = currentUser?.isAdmin || c.userId === currentUser?.id;
                          return (
                            <div key={c.id} className="flex gap-2 group">
                              <div className="flex-1 bg-secondary/50 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-xs font-semibold">{commentUser?.name || c.authorName || 'Unbekannt'}</span>
                                  <span className="text-[10px] text-muted-foreground">{c.timestamp.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                <p className="text-sm">{c.text}</p>
                              </div>
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteComment(c.id)}
                                  className="self-center opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-1"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mb-4">Noch keine Kommentare. Sei der Erste!</p>
                    )}

                    {/* Quick replies */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {QUICK_COMMENTS.map((qc) => (
                        <button
                          key={qc}
                          className="px-2.5 py-1 text-xs rounded-full border border-input bg-background hover:bg-accent hover:text-primary transition-colors"
                          onClick={() => handleComment(qc)}
                          disabled={isCommenting}
                        >
                          {qc}
                        </button>
                      ))}
                    </div>

                    {/* Comment input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(commentText); } }}
                        placeholder="Kommentar schreiben..."
                        className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        disabled={isCommenting}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleComment(commentText)}
                        disabled={!commentText.trim() || isCommenting}
                      >
                        <Send size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {lightboxOpen && liveSelectedPost && (
        <Lightbox
          images={postImages(liveSelectedPost)}
          startIndex={detailIdx}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default BragBoardPage;
